import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { StepLinearConnector } from './StepLinearConnector';
import { useProgressStepper } from './ProgressStepperContext';
import StepContext from './StepContext';
import { StepNonLinearConnector } from './StepNonLinearConnector';

/**
 * A `Step` component is a container for the button, label and content of each step in
 * a [ProgressStepper](#progressstepper) component.
 *
 * The `useStep` hook allows access to the following context values on `Step` sub-components:
 * - `align`
 * - `index`
 * - `active`
 * - `completed`
 * - `status`
 * - `last`
 * - `disabled`
 *
 * Use `Step` with the following sub-components: [StepLabel](#steplabel),
 * [StepButton](#stepbutton), [StepContent](#stepcontent)
 *
 * Usage:
 *
 * ```jsx
 * import { Step } from '@one-thd/sui-atomic-components';
 * ```
 */
const Step = React.forwardRef((props, ref) => {

  const {
    index,
    last,
    active: activeProp,
    disabled: disabledProp,
    completed: completedProp,
    status,
    align = 'center',
    'aria-label': ariaLabelProp,
    children,
    ...rest
  } = props;

  const {
    isVertical,
    activeStep,
    connector,
    nonLinear
  } = useProgressStepper();

  const stepClasses = classNames('sui-m-0 sui-relative sui-flex', {
    'sui-flex-col sui-py-2': !isVertical,
    'sui-py-2': isVertical
  });

  let [active = false, completed = false, disabled = false] = [
    activeProp,
    completedProp,
    disabledProp,
  ];

  if (activeStep === index) {
    active = activeProp !== undefined ? activeProp : true;
  } else if (!nonLinear && activeStep > index) {
    completed = completedProp !== undefined ? completedProp : true;
  }

  const contextValue = useMemo(() => ({
    align, index, active, completed, status, last, disabled
  }),
  [align, index, active, completed, status, last, disabled]
  );

  const stepConnector = connector
    || (!nonLinear && <StepLinearConnector />)
    || <StepNonLinearConnector />;

  const stepStatuses = [ariaLabelProp];
  if (active) {
    stepStatuses.push('Active step.');
  }
  if (completed) {
    stepStatuses.push('Completed.');
  }
  const ariaLabel = stepStatuses.filter(Boolean).join(' ');
  return (
    <StepContext.Provider value={contextValue}>
      <li
        className={stepClasses}
        ref={ref}
        aria-current={active ? 'step' : null}
        aria-label={ariaLabel}
        {...rest}
      >
        {stepConnector}
        {children}
      </li>
    </StepContext.Provider>
  );
});

Step.displayName = 'Step';

Step.propTypes = {
  /**
   * The states of validation for the Step component.
   */
  status: PropTypes.oneOf(['warning', 'error']),
  /**
   * Set the text-align on the component.
   * @default 'center'
   */
  align: PropTypes.oneOf(['center', 'start', 'end']),
  /**
   * Set the step as active. Is passed to child components.
   */
  active: PropTypes.bool,
  /**
   * If true, the step is disabled, will also disable the button if `StepButton` is a child of `Step`.
   * Is passed to child components.
   */
  disabled: PropTypes.bool,
  /**
   * Mark the step as completed. Is passed down to child components.
   */
  completed: PropTypes.bool,
  /**
   * Should be `Step` sub-components such as `StepLabel` and `StepButton`.
   */
  children: PropTypes.node,
  /**
   * If true, the Step is displayed as rendered last.
   * The prop defaults to the value inherited from the parent `ProgressStepper` component.
   */
  last: PropTypes.bool,
  /**
   * The zero-based index of the step.
   * The prop defaults to the value inherited from the parent `ProgressStepper` component.
   */
  index: PropTypes.number,
  /**
   * @ignore
   */
  'aria-label': PropTypes.string
};

export { Step };